import { useMemo } from "react"
import type { ErrorOption, FieldNamesMarkedBoolean, FieldPath, SubmitHandler } from "react-hook-form"

import type {
  ClassificationCreateFailure,
  ClassificationCreateInput,
  ClassificationUpdateFailure,
  ClassificationUpdateInput,
} from "@/graphql/codegen/graphql"
import { NotificationType } from "@/schemas/notification"
import type { ProductClassificationFormValues } from "@/screens/ManageProducts/components/ProductClasses/forms/classSchema"
import useProductClassification from "@/screens/ManageProducts/components/ProductClasses/hooks/useProductClassification"
import { useNotificationsStore } from "@/stores/useNotificationsStore"

const useProductClassificationActions = ({
  name,
  onSuccess,
  onClose,
  setError,
}: {
  name?: string
  onSuccess?: () => void
  onClose?: () => void
  setError: (name: FieldPath<ProductClassificationFormValues>, error: ErrorOption) => void
}): {
  productClassificationNameList: string[]
  createProductClassification: SubmitHandler<ClassificationCreateInput>
  updateProductClassification: (
    data: ClassificationUpdateInput,
    dirtyFields?: Partial<Readonly<FieldNamesMarkedBoolean<ProductClassificationFormValues>>>
  ) => Promise<any>
} => {
  const notify = useNotificationsStore((state) => state.enqueueNotification)
  const shouldQuery = name?.length && name.length >= 2

  const { productClassList, createClass, updateClass } = useProductClassification({
    query: name,
    pause: !shouldQuery,
  })

  const productClassificationNameList = useMemo(() => {
    if (!shouldQuery) {
      return []
    }
    return productClassList.map((classification) => classification.name)
  }, [productClassList, shouldQuery])

  const createProductClassification = async (input: ClassificationCreateInput) => {
    const { error, data } = await createClass({
      input,
    })

    const hasError =
      error?.message ||
      (data?.classification.create.__typename === "ClassificationCreateFailure" &&
        Boolean(data?.classification.create.error))

    if (hasError) {
      setError("name", {
        type: "server",
        message: error?.message ?? (data?.classification.create as ClassificationCreateFailure).error?.message,
      })
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to create classification`,
      })
      return
    }

    onSuccess?.()
    notify({
      type: NotificationType.Success,
      silent: false,
      content: "Product class successfully created",
    })
    onClose?.()
  }

  const updateProductClassification = async (
    input: ClassificationUpdateInput,
    changedFields?: Partial<Readonly<FieldNamesMarkedBoolean<ProductClassificationFormValues>>>
  ) => {
    const { error, data } = await updateClass({
      input,
    })

    const messageSuccess = changedFields?.productManager
      ? "Product manager successfully updated"
      : "Product class successfully updated"

    const hasError =
      error?.message ||
      (data?.classification.update.__typename === "ClassificationUpdateFailure" &&
        Boolean(data?.classification.update.error))

    if (hasError) {
      setError("name", {
        type: "server",
        message: error?.message ?? (data?.classification.update as ClassificationUpdateFailure).error?.message,
      })
      notify({
        type: NotificationType.Error,
        silent: false,
        message: error?.message ?? `Failed to update classification`,
      })
      return
    }

    onSuccess?.()
    notify({
      type: NotificationType.Success,
      silent: false,
      content: messageSuccess,
    })
    onClose?.()
  }

  return {
    productClassificationNameList,
    createProductClassification,
    updateProductClassification,
  }
}

export default useProductClassificationActions
